import { useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';

/**
 * 共通モーダル（D-094）。
 *
 * 「一覧の行を押した結果」を出す場所として使う。一覧の下に出す方式は、
 * 行数が多いと結果が画面外になり「押しても何も起きない」ように見えるため
 * （利用者フィードバック）。確認ダイアログ（ConfirmDialog）もこの土台を使う。
 *
 * 閉じ方は3通り: 右上の×／背景のクリック／Escape。
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 右下に並べる操作ボタン（省略時はフッター自体を出さない） */
  footer?: ReactNode;
  /** 表や明細など横幅が必要な内容のとき */
  wide?: boolean;
}): ReactElement | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      // 背景（この要素そのもの）のクリックだけで閉じる。中身のクリックでは閉じない
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`flex max-h-full w-full flex-col rounded-sm border border-wpborder bg-white shadow-lg ${
          wide ? 'max-w-2xl' : 'max-w-md'
        }`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-wpborder px-5 py-4">
          <h2 id={titleId} className="text-wppanel font-semibold text-wptext-strong">
            {title}
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="shrink-0 rounded-sm px-1 text-wptext-muted hover:text-wptext-strong"
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-wpborder px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
